import React, { useMemo } from 'react';
import { Text, View, Image } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useRoute } from '@react-navigation/native';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';

import rteIcon from '@assets/img/rte_preview.png';
import docxIcon from '@assets/img/docx_preview.png';
import pptxIcon from '@assets/img/pptx_preview.png';
import pdfIcon from '@assets/img/pdf_preview.png';
import unknownExtensionIcon from '@assets/img/unknown_extension.png';

import { ColorFunction } from '@styles/colors/types';
import { scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { OP_TAG_NAMING, TAG_KEYS } from '@components/imageHandling/common';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { useBuildLinkListByPerspective } from '@components/operatorPanel/hooks/useBuildlLinkListByPerspective';
import { ODataDocument } from '@v2/components/imageHandling/domain/types';

interface NotePreviewItemProps {
  note: ODataDocument;
  C: ColorFunction;
}

const getDocumentIcon = (extension?: string) => {
  switch (extension) {
    case 'rtf':
      return rteIcon;
    case 'docs':
      return docxIcon;
    case 'pptx':
      return pptxIcon;
    case 'pdf':
      return pdfIcon;
    default:
      return unknownExtensionIcon;
  }
};

const NotePreviewItem: React.FC<NotePreviewItemProps> = ({ C, note }) => {
  const route = useRoute();
  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];

  const imagesDocumentsReferences = useBuildLinkListByPerspective(selectedJobId);

  const linksSet = useMemo(() => {
    const links = new Set<`${string}-${string}`>();

    imagesDocumentsReferences.forEach(({ scopeId, scopeReferenceId }) => links.add(`${scopeId}-${scopeReferenceId}`));

    return links;
  }, [imagesDocumentsReferences]);

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: C('Background/Light/Field on Panel [mobile]'),
        borderRadius: scaleOP(SCREEN_BORDER_RADIUS),
        paddingHorizontal: scaleOP(18),
        paddingTop: scaleOP(16),
        paddingBottom: 0,
        overflow: 'hidden',
      }}
    >
      <Image style={{ width: scaleOP(18), height: scaleOP(26) }} source={getDocumentIcon(note?.extensionOriginal)} />
      <Text
        style={[
          fontOP['Headline Large'],
          {
            color: C('Label/Light/Primary'),
            alignSelf: 'center',
            flexShrink: 0,
            marginLeft: scaleOP(5),
            fontWeight: 600,
            paddingRight: scaleOP(6),
          },
        ]}
      >
        {(note?.fileNameOverride ?? note?.title ?? note?.fileName).replace('.rtf', '')}
      </Text>
      <FlashList
        data={note.links}
        renderItem={({ item: link }) => {
          const isInScope = linksSet.has(`${link.scopeId}-${link.scopeReferenceId}`);

          if (!isInScope) {
            return null;
          }

          return (
            <View
              key={link.linkId}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: scaleOP(6),
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: C('Selected/Light/Secondary'),
                borderWidth: scaleOP(1),
                borderColor: C('Hairline/Light/No trans [mobile]'),
                borderRadius: scaleOP(SCREEN_BORDER_RADIUS),
                paddingRight: scaleOP(12),
                paddingLeft: scaleOP(6),
              }}
            >
              <Svg width={scaleOP(24)} height={scaleOP(24)} icon="attached" color="Label/Light/Primary" />
              <Text style={[fontOP['Subhead 2'], { color: C('Label/Light/Primary'), fontWeight: 400 }]}>{`${i18n.t(
                OP_TAG_NAMING?.[TAG_KEYS?.[link.scopeId]]
              )} ${link.scopeReferenceName}`}</Text>
            </View>
          );
        }}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ marginLeft: 'auto', flex: 1 }}
        contentContainerStyle={{
          display: 'flex',
          flexDirection: 'row-reverse',
          justifyContent: 'flex-start',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: scaleOP(9),
        }}
      />
    </View>
  );
};

export default ColorWrapper(NotePreviewItem);
